import { useMemo } from 'react';
import type { MyProjectItem } from '@features/projects/types/project.types';
import type { SessionMineItem } from '@features/sessions/types/session.types';
import { groupByRecency, type RecencyGroup } from '../utils/grouping';
import { useSearchCorpus } from './useSearchCorpus';

/** One rail entry: a project and the caller's own sittings inside it. */
export interface HistoryProject {
  readonly project: MyProjectItem;
  readonly sessions: readonly SessionMineItem[];
  /** Last activity, from the project's own stamp or its newest sitting. Null for
   *  a project shared with the caller that they have not sat in yet. */
  readonly lastAt: string | null;
}

export interface HistoryTree {
  readonly groups: readonly RecencyGroup<HistoryProject>[];
  readonly total: number;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly refetch: () => void;
}

function newestOf(sessions: readonly SessionMineItem[]): string | null {
  return sessions.reduce<string | null>((newest, session) => {
    const at = session.ended_at ?? session.started_at;
    return !newest || at > newest ? at : newest;
  }, null);
}

/**
 * The rail's feed: projects, newest first, each with its own sittings beneath.
 *
 * By project rather than a flat list of sittings, for the reason the history
 * screen already works that way — people remember "the warehouse thing", not
 * session 4. The nesting is what makes a sitting reachable without giving it the
 * top-level weight of a project.
 *
 * Assembled here from two endpoints. `GET /me/recents` would return exactly this
 * shape in one call, at which point this hook keeps its signature and loses its
 * body.
 */
export function useHistoryTree(): HistoryTree {
  const { projects, sessions, isPending, isError, refetch } = useSearchCorpus();

  const groups = useMemo(() => {
    const byProject = new Map<number, SessionMineItem[]>();
    for (const session of sessions) {
      const existing = byProject.get(session.project.id);
      if (existing) existing.push(session);
      else byProject.set(session.project.id, [session]);
    }

    const entries = projects.map<HistoryProject>((project) => {
      const own = (byProject.get(project.id) ?? [])
        .slice()
        .sort((a, b) => (b.ended_at ?? b.started_at).localeCompare(a.ended_at ?? a.started_at));
      return {
        project,
        sessions: own,
        lastAt: project.last_session_at ?? newestOf(own),
      };
    });

    // Undated projects sort last rather than first, which is where an empty
    // string would put them in a plain descending compare.
    entries.sort((a, b) => {
      if (!a.lastAt && !b.lastAt) return a.project.name.localeCompare(b.project.name);
      if (!a.lastAt) return 1;
      if (!b.lastAt) return -1;
      return b.lastAt.localeCompare(a.lastAt);
    });

    return groupByRecency(entries, (entry) => entry.lastAt);
  }, [projects, sessions]);

  return {
    groups,
    total: projects.length,
    isPending,
    isError,
    refetch,
  };
}
